/**
 * Busca e filtros da tela Flows (AB#86605). O status segue o que o card mostra:
 * instalado e publicado, rascunho (instalado sem versão publicada) ou disponível
 * para instalar — "em breve" conta como disponível, é o mesmo lugar na loja.
 */

export type FlowsStatusFilter = "all" | "installed" | "draft" | "available";

export interface FlowsFilterState {
  query: string;
  status: FlowsStatusFilter;
  /** Tipos de gatilho ativo; vazio = qualquer um. */
  triggers: string[];
}

export interface FilterableFlow {
  name: string;
  status: string;
  draft?: boolean;
  triggers?: Array<{ type: string }>;
}

export const EMPTY_FLOWS_FILTER: FlowsFilterState = { query: "", status: "all", triggers: [] };

export function isEmptyFlowsFilter(filter: FlowsFilterState): boolean {
  return filter.query.trim() === "" && filter.status === "all" && filter.triggers.length === 0;
}

function normalize(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

function statusOf(flow: FilterableFlow): Exclude<FlowsStatusFilter, "all"> | null {
  if (flow.status === "installed") return flow.draft ? "draft" : "installed";
  if (flow.status === "available" || flow.status === "soon") return "available";
  return null;
}

/**
 * Os tipos de gatilho que existem na lista, em ordem fixa. O filtro só oferece
 * o que devolve alguma coisa — a mesma regra das etiquetas de Skills.
 */
export function triggerTypesOf(flows: FilterableFlow[]): string[] {
  const order = ["schedule", "webhook", "manual", "email", "storage"];
  const present = new Set(flows.flatMap((flow) => (flow.triggers ?? []).map((t) => t.type)));
  return [...present].sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib) || a.localeCompare(b);
  });
}

/** `describe` devolve o texto já traduzido do card, que a busca também olha. */
export function filterFlows<T extends FilterableFlow>(
  flows: T[],
  filter: FlowsFilterState,
  describe: (flow: T) => string | undefined,
): T[] {
  const terms = normalize(filter.query).split(/\s+/).filter(Boolean);
  return flows.filter((flow) => {
    if (filter.status !== "all" && statusOf(flow) !== filter.status) return false;
    if (filter.triggers.length > 0 && !(flow.triggers ?? []).some((t) => filter.triggers.includes(t.type)))
      return false;
    if (terms.length === 0) return true;
    const haystack = normalize([flow.name, describe(flow)].filter(Boolean).join(" "));
    return terms.every((term) => haystack.includes(term));
  });
}
